import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { InsuranceCardUpload, type SelectedImage } from "@/components/insurance/InsuranceCardUpload";
import {
  ERROR_INPUT_CLASS,
  FieldError,
  isValidPhone,
  phoneDigits,
  formatPhoneDisplay,
  cn,
} from "@/components/insurance/insuranceForm";
import { FINANCIAL_ROUTES, financialHandoff } from "@/utils/financialFlow";
import {
  INITIAL_HEALTH,
  isAccidentOrWcRecord,
  RELATION_OPTIONS,
  normalizeRelation,
  relationRequiresName,
} from "@/hooks/useInsuranceRecord";
import { useInsuranceStore, type InsuranceInfo, type IInsImage } from "@/store/insurance";
import { markIntakeStepDone } from "@/store/intakeProgress";
import { useTaskStore } from "@/store/task";
import { useProfileStore } from "@/store/profile";
import { useDemographicsStore } from "@/store/demographics";
import { usePrimaryComplaintStore } from "@/store/primaryComplaint";

const MAX_INSURANCES = 3;
const SAVE_DEBOUNCE_MS = 600;

const emptyImage = (): IInsImage => ({ id: 0, description: "", fileID: 0, fileName: "", base64: "", extension: "" });

/** Data-URL mime from a stored file extension, so PDFs preview as PDFs (not broken images). */
function mimeForExtension(ext?: string): string {
  const e = (ext ?? "").toLowerCase();
  if (e === "pdf") return "application/pdf";
  if (e === "png") return "image/png";
  if (e === "gif") return "image/gif";
  if (e === "webp") return "image/webp";
  return "image/jpeg";
}

/**
 * Turn whatever DownloadInsuranceImage returns into a browser-renderable data URL.
 * Tolerant of: an already-formed `data:` URL (don't double-prefix — that broke both
 * photos and PDFs on refresh), a wrapping `{ base64 }` object, surrounding quotes,
 * and a missing extension (type is inferred from the base64 magic bytes).
 */
function toInsuranceDataUrl(raw: unknown, extension?: string): string {
  if (!raw) return "";
  let b64 =
    typeof raw === "string"
      ? raw
      : (raw as { base64?: string; data?: string }).base64 ??
        (raw as { data?: string }).data ??
        "";
  b64 = String(b64).trim().replace(/^"+|"+$/g, "");
  if (!b64) return "";
  if (b64.startsWith("data:")) return b64; // already a full data URL — use as-is
  const mime =
    b64.startsWith("JVBERi0") ? "application/pdf" : // "%PDF-"
    b64.startsWith("/9j/") ? "image/jpeg" :          // JPEG
    b64.startsWith("iVBOR") ? "image/png" :          // PNG
    b64.startsWith("R0lGOD") ? "image/gif" :         // GIF
    b64.startsWith("UklGR") ? "image/webp" :         // WEBP (RIFF)
    mimeForExtension(extension);
  return `data:${mime};base64,${b64}`;
}

interface Card {
  key: number;
  id: number;
  relation: string;
  carrier: string;
  firstName: string;
  mi: string;
  lastName: string;
  dob: string;
  idNum: string;
  groupNo: string;
  carrierPhone: string;
  front: IInsImage;
  back: IInsImage;
}

type CardField =
  | "relation" | "carrier" | "firstName" | "mi" | "lastName"
  | "dob" | "idNum" | "groupNo" | "carrierPhone";

function cardFromRecord(key: number, r: InsuranceInfo): Card {
  return {
    key,
    id: r.id ?? 0,
    relation: normalizeRelation(r.relation),
    carrier: r.carrier ?? "",
    firstName: r.firstName ?? "",
    mi: r.mi ?? "",
    lastName: r.lastName ?? "",
    dob: r.dob ?? "",
    idNum: r.idNum ?? "",
    groupNo: r.groupNo ?? "",
    carrierPhone: (r.carrierPhone ?? "").replace(/\D/g, ""),
    front: { ...emptyImage(), ...(r.images?.front ?? {}) },
    back: { ...emptyImage(), ...(r.images?.back ?? {}) },
  };
}

export function HealthInsurancePage() {
  const navigate = useNavigate();
  const fetchInsuranceInfo = useInsuranceStore((s) => s.fetchInsuranceInfo);
  const updateInsuranceInfo = useInsuranceStore((s) => s.updateInsuranceInfo);
  const updateInsuranceInfoField = useInsuranceStore((s) => s.updateInsuranceInfoField);
  const uploadInsuranceImage = useInsuranceStore((s) => s.uploadInsuranceImage);
  const downloadInsuranceImage = useInsuranceStore((s) => s.downloadInsuranceImage);
  const deleteInsuranceInfo = useInsuranceStore((s) => s.deleteInsuranceInfo);

  const portalCaseHistoryFlow = useTaskStore((s) => s.portalCaseHistoryFlow);
  const wizardSteps = useTaskStore((s) => s.wizardSteps);
  const profileDob = useProfileStore((s) => s.currentPatient?.dob);
  const demoDob = useDemographicsStore((s) => s.demographics?.dob);
  const patientDob = profileDob || demoDob;
  const startNewComplaint = usePrimaryComplaintStore((s) => s.startNewComplaint);

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<Card[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  // Accordion: at most one card open at a time. `null` falls back to the first
  // card; `-1` means every card is collapsed. A lone card is always open.
  const [expandedKey, setExpandedKey] = useState<number | null>(null);

  const cardsRef = useRef<Card[]>([]);
  cardsRef.current = cards;
  const keyCounter = useRef(0);
  const nextKey = () => ++keyCounter.current;
  const createPromises = useRef<Record<number, Promise<number> | undefined>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingSaves = useRef<Record<string, { key: number; field: CardField; value: string }>>({});

  const emptyCard = useCallback((): Card => ({
    key: nextKey(),
    id: 0,
    relation: "",
    carrier: "",
    firstName: "",
    mi: "",
    lastName: "",
    dob: "",
    idNum: "",
    groupNo: "",
    carrierPhone: "",
    front: emptyImage(),
    back: emptyImage(),
  }), []);

  // Prefill: fetch fresh, keep only health records (exclude auto/wc), pull card images.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = (await fetchInsuranceInfo()) as InsuranceInfo[] | null | undefined;
        const health = Array.isArray(list) ? list.filter((r) => !isAccidentOrWcRecord(r)) : [];
        const built: Card[] = [];
        for (const r of health) {
          const card = cardFromRecord(nextKey(), r);
          for (const side of ["front", "back"] as const) {
            const fileID = card[side].fileID;
            if (fileID > 0) {
              try {
                const raw = (await downloadInsuranceImage(fileID)) as unknown;
                const url = toInsuranceDataUrl(raw, card[side].extension);
                if (url) card[side].base64 = url;
              } catch {
                /* image best-effort */
              }
            }
          }
          built.push(card);
        }
        if (cancelled) return;
        setCards(built.length ? built : [emptyCard()]);
      } catch {
        if (!cancelled) setCards([emptyCard()]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    const t = timers.current;
    return () => {
      cancelled = true;
      Object.values(t).forEach(clearTimeout);
    };
    // Run once — store fns are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchCard = (key: number, patch: Partial<Card>) =>
    setCards((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const effectiveExpandedKey = expandedKey === null ? cards[0]?.key : expandedKey;
  const isExpanded = (key: number) => cards.length === 1 || effectiveExpandedKey === key;
  const toggleExpanded = (key: number) =>
    setExpandedKey((cur) => {
      const eff = cur === null ? cardsRef.current[0]?.key : cur;
      return eff === key ? -1 : key; // collapse if already open, else open just this one
    });

  const ensureCard = useCallback(async (key: number): Promise<number> => {
    const existing = cardsRef.current.find((c) => c.key === key);
    if (existing?.id) return existing.id;
    const inflight = createPromises.current[key];
    if (inflight !== undefined) return inflight;
    const p = (async () => {
      const created = (await updateInsuranceInfo(INITIAL_HEALTH)) as InsuranceInfo | null;
      const newId = created?.id ?? 0;
      if (newId) patchCard(key, { id: newId });
      return newId;
    })();
    createPromises.current[key] = p;
    try {
      return await p;
    } finally {
      delete createPromises.current[key];
    }
  }, [updateInsuranceInfo]);

  const runCardSave = useCallback(async (tkey: string) => {
    const p = pendingSaves.current[tkey];
    if (!p) return;
    delete pendingSaves.current[tkey];
    try {
      const id = await ensureCard(p.key);
      if (!id) { toast.error("Something went wrong saving your info."); return; }
      await updateInsuranceInfoField(id, p.field, p.value);
    } catch {
      toast.error("Something went wrong saving your info.");
    }
  }, [ensureCard, updateInsuranceInfoField]);

  const saveField = useCallback((key: number, field: CardField, value: string) => {
    const tkey = `${key}:${field}`;
    pendingSaves.current[tkey] = { key, field, value };
    clearTimeout(timers.current[tkey]);
    timers.current[tkey] = setTimeout(() => {
      delete timers.current[tkey];
      void runCardSave(tkey);
    }, SAVE_DEBOUNCE_MS);
  }, [runCardSave]);

  /** Persist any pending debounced edits now (call before navigating away). */
  const flushSaves = useCallback(async () => {
    const keys = Object.keys(pendingSaves.current);
    for (const k of keys) {
      clearTimeout(timers.current[k]);
      delete timers.current[k];
    }
    await Promise.all(keys.map((k) => runCardSave(k)));
  }, [runCardSave]);

  const update = (key: number, field: CardField, value: string) => {
    patchCard(key, { [field]: value } as Partial<Card>);
    const ekey = `${key}:${field}`;
    if (errors[ekey]) setErrors((e) => ({ ...e, [ekey]: "" }));
    saveField(key, field, value);
  };

  const onSelectImage = async (key: number, side: "front" | "back", img: SelectedImage) => {
    const ukey = `${key}:${side}`;
    setUploading((u) => ({ ...u, [ukey]: true }));
    try {
      const id = await ensureCard(key);
      if (!id) { toast.error("Please enter details first."); return; }
      const meta = (await uploadInsuranceImage(
        id, img.base64Raw, `${side} of insurance card`, side === "back", img.extension
      )) as Partial<IInsImage> | null;
      patchCard(key, {
        [side]: { ...emptyImage(), ...(meta ?? {}), base64: img.dataUrl },
      } as Partial<Card>);
    } catch {
      toast.error("Could not upload that photo.");
    } finally {
      setUploading((u) => ({ ...u, [ukey]: false }));
    }
  };

  const addCard = () => {
    if (cards.length >= MAX_INSURANCES) return;
    const c = emptyCard();
    setCards((cs) => [...cs, c]);
    setExpandedKey(c.key); // open the new one, collapse the others
  };

  const removeCard = async (key: number) => {
    const card = cardsRef.current.find((c) => c.key === key);
    setCards((cs) => cs.filter((c) => c.key !== key));
    setExpandedKey((cur) => (cur === key ? null : cur)); // fall back to first if we closed the open one
    if (card?.id) {
      try {
        await deleteInsuranceInfo(card.id);
      } catch {
        toast.error("Could not remove that insurance.");
      }
    }
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    for (const c of cards) {
      if (!c.relation) next[`${c.key}:relation`] = "Required";
      if (!c.carrier.trim()) next[`${c.key}:carrier`] = "Required";
      if (relationRequiresName(c.relation)) {
        if (!c.firstName.trim()) next[`${c.key}:firstName`] = "Required";
        if (!c.lastName.trim()) next[`${c.key}:lastName`] = "Required";
        if (!c.dob) next[`${c.key}:dob`] = "Required";
      }
      if (!c.idNum.trim()) next[`${c.key}:idNum`] = "Required";
      if (!c.groupNo.trim()) next[`${c.key}:groupNo`] = "Required";
      if (!c.carrierPhone.trim()) next[`${c.key}:carrierPhone`] = "Required";
      else if (!isValidPhone(c.carrierPhone)) next[`${c.key}:carrierPhone`] = "Enter a valid 10-digit phone.";
    }
    setErrors(next);
    // Open the first card that has a problem so its inline errors are visible.
    const firstBad = cards.find((c) => Object.keys(next).some((k) => k.startsWith(`${c.key}:`)));
    if (firstBad) setExpandedKey(firstBad.key);
    return Object.keys(next).length === 0;
  };

  const finish = async () => {
    await flushSaves();
    markIntakeStepDone("financial");
    const { path, startComplaint } = financialHandoff({ portalCaseHistoryFlow, wizardSteps, patientDob });
    if (startComplaint) startNewComplaint();
    navigate(path);
  };
  const goBack = async () => {
    await flushSaves();
    navigate(FINANCIAL_ROUTES.q2);
  };
  const onContinue = async () => {
    if (validate()) await finish();
  };
  const err = (key: number, field: string) => errors[`${key}:${field}`];

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content financial-responsibility-content flex justify-center px-4 py-6">
        <div className="complaint-history-card health-insurance-card">
          <div className="mb-5 flex items-start justify-between gap-3 border-b border-slate-100 pb-4">
            <div className="min-w-0">
              <h2 className="text-[20px] font-extrabold text-healthsource-dark">Health Insurance</h2>
              <p className="mt-1 text-[13px] text-healthsource-gray">
                Add up to {MAX_INSURANCES} insurances. Photos are optional. You can also skip this for now.
              </p>
            </div>
            {cards.length < MAX_INSURANCES && (
              <Button type="button" variant="outline" onClick={addCard} className="shrink-0">
                <i className="fas fa-plus mr-1.5" /> Add Insurance
              </Button>
            )}
          </div>

          {loading ? (
            <p className="py-8 text-center text-[13px] text-healthsource-gray">Loading…</p>
          ) : (
            <div className="space-y-4">
              {cards.map((c, index) => {
                const needsName = relationRequiresName(c.relation);
                const hasId = c.id > 0;
                const expanded = isExpanded(c.key);
                const collapsible = cards.length > 1;
                const cardHasError = Object.keys(errors).some(
                  (k) => k.startsWith(`${c.key}:`) && errors[k]
                );
                return (
                  <div
                    key={c.key}
                    className={cn(
                      "overflow-hidden rounded-xl border bg-slate-50/60 transition-colors",
                      expanded ? "border-healthsource-navy/30" : "border-slate-200"
                    )}
                  >
                    {/* Header — always visible. Tap to expand/collapse when there's more than one. */}
                    <div className="flex items-center justify-between gap-2 p-3.5 sm:p-4">
                      <button
                        type="button"
                        onClick={() => collapsible && toggleExpanded(c.key)}
                        className={cn(
                          "-m-1.5 flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1.5 text-left transition-colors",
                          collapsible && "cursor-pointer hover:bg-slate-100/70"
                        )}
                        aria-expanded={expanded}
                        {...(!collapsible ? { tabIndex: -1 } : {})}
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-healthsource-navy/10 text-healthsource-navy">
                          <i className="fas fa-id-card text-[14px]" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-semibold text-healthsource-gray">
                            Insurance {index + 1}
                          </span>
                          <span className="block truncate text-[14px] font-bold text-healthsource-dark">
                            {c.carrier.trim() || <span className="font-semibold text-healthsource-gray">Not added yet</span>}
                          </span>
                        </span>
                        {collapsible && !expanded && cardHasError && (
                          <i className="fas fa-circle-exclamation text-[13px] text-red-500" aria-label="Needs attention" />
                        )}
                        {collapsible && (
                          <i
                            className={cn(
                              "fas fa-chevron-down text-[12px] text-healthsource-gray transition-transform",
                              !expanded && "-rotate-90"
                            )}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                      {cards.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCard(c.key)}
                          className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg px-2 text-[14px] font-semibold text-healthsource-gray hover:bg-slate-100 hover:text-healthsource-dark focus:outline-none focus:ring-2 focus:ring-healthsource-navy/30"
                          aria-label={`Remove insurance ${index + 1}`}
                        >
                          <i className="fas fa-trash text-[13px]" aria-hidden="true" /> Remove
                        </button>
                      )}
                    </div>

                    {expanded && (
                      <div className="border-t border-slate-200 bg-white p-3 sm:p-5">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          {/* Row 1: Insurance Carrier | Insurance Phone */}
                          <div>
                            <Label htmlFor={`hi-${c.key}-carrier`} required>Insurance Carrier</Label>
                            <Input
                              id={`hi-${c.key}-carrier`}
                              placeholder="Insurance Carrier"
                              value={c.carrier}
                              onChange={(e) => update(c.key, "carrier", e.target.value)}
                              className={cn(err(c.key, "carrier") && ERROR_INPUT_CLASS)}
                            />
                            <FieldError message={err(c.key, "carrier")} />
                          </div>
                          <div>
                            <Label htmlFor={`hi-${c.key}-carrierPhone`} required>Insurance Phone</Label>
                            <Input
                              id={`hi-${c.key}-carrierPhone`}
                              type="tel"
                              inputMode="tel"
                              placeholder="(XXX) XXX-XXXX"
                              value={formatPhoneDisplay(c.carrierPhone)}
                              onChange={(e) => update(c.key, "carrierPhone", phoneDigits(e.target.value))}
                              className={cn(err(c.key, "carrierPhone") && ERROR_INPUT_CLASS)}
                            />
                            <FieldError message={err(c.key, "carrierPhone")} />
                          </div>

                          {/* Row 2: Member ID | Group Number */}
                          <div>
                            <Label htmlFor={`hi-${c.key}-idNum`} required>Member ID</Label>
                            <Input
                              id={`hi-${c.key}-idNum`}
                              placeholder="Member ID"
                              value={c.idNum}
                              onChange={(e) => update(c.key, "idNum", e.target.value)}
                              className={cn(err(c.key, "idNum") && ERROR_INPUT_CLASS)}
                            />
                            <FieldError message={err(c.key, "idNum")} />
                          </div>
                          <div>
                            <Label htmlFor={`hi-${c.key}-groupNo`} required>Group Number</Label>
                            <Input
                              id={`hi-${c.key}-groupNo`}
                              placeholder="Group Number"
                              value={c.groupNo}
                              onChange={(e) => update(c.key, "groupNo", e.target.value)}
                              className={cn(err(c.key, "groupNo") && ERROR_INPUT_CLASS)}
                            />
                            <FieldError message={err(c.key, "groupNo")} />
                          </div>

                          {/* Policy Holder — and, only if not the patient, their name + DOB */}
                          <div className="sm:col-span-2 sm:max-w-xs">
                            <Label htmlFor={`hi-${c.key}-relation`} required>Policy Holder</Label>
                            <Select
                              id={`hi-${c.key}-relation`}
                              value={c.relation}
                              onChange={(e) => update(c.key, "relation", e.target.value)}
                              className={cn(err(c.key, "relation") && ERROR_INPUT_CLASS)}
                            >
                              <option value="">Select</option>
                              {RELATION_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </Select>
                            <FieldError message={err(c.key, "relation")} />
                          </div>

                          {needsName && (
                            <>
                              <div className="sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_5rem_1fr]">
                                <div>
                                  <Label htmlFor={`hi-${c.key}-firstName`} required>First Name</Label>
                                  <Input
                                    id={`hi-${c.key}-firstName`}
                                    placeholder="First Name"
                                    value={c.firstName}
                                    onChange={(e) => update(c.key, "firstName", e.target.value)}
                                    className={cn(err(c.key, "firstName") && ERROR_INPUT_CLASS)}
                                  />
                                  <FieldError message={err(c.key, "firstName")} />
                                </div>
                                <div>
                                  <Label htmlFor={`hi-${c.key}-mi`}>MI</Label>
                                  <Input
                                    id={`hi-${c.key}-mi`}
                                    placeholder="MI"
                                    maxLength={1}
                                    value={c.mi}
                                    onChange={(e) => update(c.key, "mi", e.target.value.slice(0, 1))}
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`hi-${c.key}-lastName`} required>Last Name</Label>
                                  <Input
                                    id={`hi-${c.key}-lastName`}
                                    placeholder="Last Name"
                                    value={c.lastName}
                                    onChange={(e) => update(c.key, "lastName", e.target.value)}
                                    className={cn(err(c.key, "lastName") && ERROR_INPUT_CLASS)}
                                  />
                                  <FieldError message={err(c.key, "lastName")} />
                                </div>
                              </div>
                              <div className="sm:max-w-xs">
                                <Label htmlFor={`hi-${c.key}-dob`} required>Policy Holder Date of Birth</Label>
                                <Input
                                  id={`hi-${c.key}-dob`}
                                  type="date"
                                  value={c.dob ? c.dob.slice(0, 10) : ""}
                                  onChange={(e) => update(c.key, "dob", e.target.value)}
                                  className={cn(err(c.key, "dob") && ERROR_INPUT_CLASS)}
                                />
                                <FieldError message={err(c.key, "dob")} />
                              </div>
                            </>
                          )}
                        </div>

                        {/* Optional insurance card photos — last */}
                        <div className="mt-5 border-t border-slate-100 pt-4">
                          <p className="mb-2 text-[13px] font-semibold text-healthsource-gray">
                            Insurance card photos <span className="font-normal">(optional)</span>
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            <InsuranceCardUpload
                              side="front"
                              image={c.front}
                              disabled={!hasId}
                              busy={uploading[`${c.key}:front`]}
                              onSelect={(img) => onSelectImage(c.key, "front", img)}
                            />
                            <InsuranceCardUpload
                              side="back"
                              image={c.back}
                              disabled={!hasId}
                              busy={uploading[`${c.key}:back`]}
                              onSelect={(img) => onSelectImage(c.key, "back", img)}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack}>Back</Button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={finish}>Skip for now</Button>
          <Button type="button" variant="primary" onClick={onContinue}>Continue</Button>
        </div>
      </WizardActionsBar>
    </div>
  );
}
